import { CreateRateItemRequest, OwnBoxPackaging, PackagingRateItemData, RateItem } from '../models/rate.types';

export function getCurrentPackagingRateItem(
  items: Array<{ id: number; type: string; data: PackagingRateItemData | Record<string, unknown> }>,
): RateItem | null {
  const packagingItems = items.filter((item) => item.type === 'packaging') as RateItem[];
  if (packagingItems.length === 0) return null;
  return [...packagingItems].sort((a, b) => b.id - a.id)[0];
}

export function packagingPriceById(data: PackagingRateItemData | undefined, packagingId: number): number | null {
  return data?.items.find((entry) => entry.packaging_id === packagingId)?.price ?? null;
}

export function buildPackagingRateItem(
  ownBoxes: OwnBoxPackaging[],
  prices: Record<number, number>,
): CreateRateItemRequest {
  return {
    title: 'Packaging',
    type: 'packaging',
    courier_id: null,
    courier_service_id: null,
    country_id: null,
    courier_zone_id: null,
    data: {
      items: ownBoxes.map((box) => ({
        packaging_id: box.id,
        price: prices[box.id] ?? box.pvp ?? 0,
      })),
    },
    notes: null,
  };
}

export function mergePackagingPrices(
  ownBoxes: OwnBoxPackaging[],
  currentItem?: RateItem | null,
): Record<number, number> {
  const currentData = currentItem?.data as PackagingRateItemData | undefined;
  const prices: Record<number, number> = {};

  for (const box of ownBoxes) {
    const existingPrice = packagingPriceById(currentData, box.id);
    prices[box.id] = existingPrice ?? box.pvp ?? 0;
  }

  return prices;
}

export function partitionRateItems(items: RateItem[]): RateItem[] {
  const currentPackaging = getCurrentPackagingRateItem(items);
  const nonPackaging = items.filter((item) => item.type !== 'packaging');

  if (!currentPackaging) {
    return nonPackaging;
  }

  return [...nonPackaging, currentPackaging];
}

export function isPackagingDataValid(data: unknown): data is PackagingRateItemData {
  if (!data || typeof data !== 'object') return false;
  const record = data as PackagingRateItemData;
  if (!Array.isArray(record.items) || record.items.length === 0) return false;
  const seenIds = new Set<number>();
  return record.items.every((entry) => {
    if (
      typeof entry.packaging_id !== 'number' ||
      entry.packaging_id <= 0 ||
      typeof entry.price !== 'number' ||
      entry.price < 0
    ) {
      return false;
    }
    if (seenIds.has(entry.packaging_id)) return false;
    seenIds.add(entry.packaging_id);
    return true;
  });
}
